import Head from 'expo-router/head';
import { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { EventCard } from '@/components/riviera/event-card';
import { HubCityChips } from '@/components/riviera/hub-city-chips';
import { HubFilterStatus, HubFilters, eventFilterGroups } from '@/components/riviera/hub-filters';
import {
  Faq,
  MoreLink,
  SectionHeading,
  landingStyles,
} from '@/components/riviera/landing-parts';
import { LoadMoreButton } from '@/components/riviera/load-more-button';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useEvents } from '@todo/core/hooks/use-events';
import { useCityListingPages } from '@/hooks/use-city-listing-pages';
import { useFilteredHub } from '@todo/core/hooks/use-filtered-hub';
import { useTheme } from '@/hooks/use-theme';
import { categories } from '@/lib/events-data';
import { DEFAULT_EVENT_FILTERS, isDefaultFilters, type EventFilters } from '@todo/core/hub-filters';
import { eventsPageFetcher, filteredEventsFetcher } from '@todo/core/hub-pages';
import { cityEventsCopy } from '@/lib/i18n/city-listings-content';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { hubCopy } from '@/lib/i18n/hub-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  CITY_LISTING_PAGES_SEED_KEY,
  EVENTS_INDEX_QUERY,
  EVENTS_INDEX_SEED_KEY,
  EVENT_CATEGORIES_SEED_KEY,
  LIST_BUCKET,
} from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';
import {
  absoluteUrl,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
} from '@todo/core/seo';

// The events hub. It exists for two reasons the homepage can't serve:
//  - the site header links "Events" here, and that link needs a destination
//    (it used to point back at the homepage);
//  - it browses the whole season: the first page of shows is baked into the
//    static HTML, "Show more" pages through the rest (useLoadMore). Crawl
//    coverage of every show comes from the per-city /[city]/events pages,
//    which the city chips link to.
// Mirrors app/[lang]/tours/index.tsx deliberately — same chip row, same
// seeding, same structured data — so the two hubs stay consistent.

export default function EventsIndexScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const listingPages = useCityListingPages();
  const { events, loading, error } = useEvents(EVENTS_INDEX_QUERY, EVENTS_INDEX_SEED_KEY);
  // Further pages continue the seed's date order — anchored on its first show,
  // see eventsPageFetcher.
  const fetchPage = useMemo(() => eventsPageFetcher(events[0]?.start_ts), [events]);
  // Filters + sort: after hydration only — see useFilteredHub. 'Any date'
  // spans the seed's own window.
  const [filters, setFilters] = useState<EventFilters>(DEFAULT_EVENT_FILTERS);
  const fetchFiltered = useMemo(
    () => filteredEventsFetcher(null, EVENTS_INDEX_QUERY.untilDays ?? 180),
    [],
  );
  const hub = useFilteredHub(
    events,
    fetchPage,
    filters,
    isDefaultFilters(filters, DEFAULT_EVENT_FILTERS),
    fetchFiltered,
  );
  const more = hub.more;
  const setFilter = useCallback(
    (id: string, value: string) => setFilters((prev) => ({ ...prev, [id]: value })),
    [],
  );
  const resetFilters = useCallback(() => setFilters(DEFAULT_EVENT_FILTERS), []);
  // The categories with upcoming shows, baked at export; on native, all.
  const [categoryIds] = useState(
    () =>
      getPrefetched<string[]>(LIST_BUCKET, EVENT_CATEGORIES_SEED_KEY) ??
      categories.map((category) => category.id),
  );

  const canonical = absoluteUrl(locale, 'events');
  // Represents the hub with its top listing's photo rather than a generic
  // brand image — real, current content beats a static placeholder.
  const ogImage = richResultImage(events[0]?.image_url);
  // Hand-written, evergreen SEO copy — see hub-content.ts.
  const copy = hubCopy(locale, 'events');
  // "Афиша Ниццы"-style anchors to every exported city page — stronger anchor
  // text than the chips' bare city names.
  const cityLinks = listingPages.events.flatMap((id) => {
    const cityCopy = cityEventsCopy(locale, id);
    return cityCopy ? [{ id, label: cityCopy.title }] : [];
  });

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{t('seo.eventsTitle')}</title>
        <meta name="description" content={t('seo.eventsDescription')} />
        <link rel="canonical" href={canonical} />
        {hreflangLinks('events').map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('seo.eventsTitle')} />
        <meta property="og:description" content={t('seo.eventsDescription')} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={t('seo.eventsTitle')} />
        <meta name="twitter:description" content={t('seo.eventsDescription')} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [{ name: t('nav.events') }]))}
        </script>
        {events.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                events.map((event) => ({
                  name: localizedTitle(event, locale),
                  url: absoluteUrl(locale, `events/${event.show_slug}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script>
        {/* Baked rows so the client's first render matches this static HTML. */}
        {listSeedScript(EVENTS_INDEX_SEED_KEY)}
        {listSeedScript(EVENT_CATEGORIES_SEED_KEY)}
        {listSeedScript(CITY_LISTING_PAGES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {t('events.headingRegion')}
            </Text>
            {copy.intro.map((paragraph, index) => (
              <Text key={index} style={[landingStyles.intro, { color: theme.text }]}>
                {paragraph}
              </Text>
            ))}

            {/* Links to each city's own page, not an in-page filter — see
                hub-city-chips.tsx. Cities without one are simply not listed. */}
            {listingPages.events.length > 0 ? (
              <HubCityChips
                cityIds={listingPages.events}
                pathname="/[lang]/[city]/events"
                locale={locale}
              />
            ) : null}

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('events.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : events.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('events.empty')}
              </Text>
            ) : (
              <>
                {/* Filters work after hydration only: the static HTML is always
                    the unfiltered seed (see useFilteredHub). */}
                <HubFilters
                  groups={eventFilterGroups(filters, t, categoryIds)}
                  onChange={setFilter}
                  onReset={resetFilters}
                />
                <HubFilterStatus
                  filtered={hub.filtered}
                  total={hub.total}
                  loading={hub.loading}
                  error={hub.error}
                  countLabel={(count) => t('hubFilters.eventsCount', { count })}
                  onRetry={hub.retry}
                  onReset={resetFilters}
                />
                <View
                  aria-busy={hub.loading}
                  style={[styles.grid, hub.loading && styles.stale]}>
                  {/* The first row is above the fold, so it loads eagerly — marking
                      an LCP candidate lazy would delay it. Everything below is
                      lazy, which is the point of the raw <img> in
                      card-image.web.tsx. */}
                  {hub.rows.map((event, index) => (
                    <EventCard key={event.id} event={event} eager={index < 4} />
                  ))}
                  {more.extra.map((event) => (
                    <EventCard key={event.id} event={event} eager={false} />
                  ))}
                </View>
                {more.canLoadMore && !hub.loading ? (
                  <LoadMoreButton onPress={more.loadMore} loading={more.loading} error={more.error} />
                ) : null}
              </>
            )}

            {/* Below the cards so the LCP image stays near the top. */}
            {copy.sections.map((section) => (
              <View key={section.heading} style={landingStyles.section}>
                <SectionHeading>{section.heading}</SectionHeading>
                {section.paragraphs.map((paragraph, index) => (
                  <Text key={index} style={[landingStyles.intro, { color: theme.text }]}>
                    {paragraph}
                  </Text>
                ))}
              </View>
            ))}

            {cityLinks.length > 0 ? (
              <View style={landingStyles.section}>
                <SectionHeading>{copy.citiesHeading}</SectionHeading>
                {cityLinks.map((link) => (
                  <MoreLink
                    key={link.id}
                    label={link.label}
                    href={{ pathname: '/[lang]/[city]/events', params: { lang: locale, city: link.id } }}
                  />
                ))}
              </View>
            ) : null}

            <Faq heading={copy.faqHeading} entries={copy.faq} />
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  status: {
    fontSize: 14,
    paddingVertical: Spacing.four,
  },
  stale: {
    opacity: 0.5,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
});
